import { useMemo, useState } from "react";

import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { ProtectedLink } from "@impero-com/common/components/protectedLink/index";
import * as Cmp from "@impero-com/common/utils/comparison";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Button,
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    getPaginationRowModel,
    Column,
    ExpandedState,
} from "@tanstack/react-table";
import { Checkbox } from "antd";

import {
    PartialControlForUser,
    PartialControlForUserListDto,
    UserGroupOverviewResourceDtoContent,
} from "api/generated/model";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import { WebRoute } from "routes";
import { canEditUserGroup, canViewControl } from "utils/actions";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import {
    USER_GROUPS_LIST_COLUMN_IDS,
    USER_GROUPS_TABLE_STATE_STORAGE_KEY,
    userGroupsListColumnFiltersCodecMap,
    userGroupsListTableDefaultValues,
} from "./consts";
import * as theme from "./Table.css";

const COLUMN_ID_EXPANDER = "expander";

export interface AssignedAs {
    isAssignee: boolean;
    isReviewer: boolean;
    isSecondReviewer: boolean;
}

/** A control the group is assigned to, with links/access resolved at build time. */
export interface ControlView {
    id: string;
    name: string;
    controlUrl: string;
    canView: boolean;
    programName: string;
    programUrl: string;
    canViewProgram: boolean;
    assignedAs: AssignedAs;
}

export interface GroupRow {
    id: number;
    name: string;
    canEdit: boolean;
    assignedToControls: number;
    controls: ControlView[];
}

function buildRows(
    groups: UserGroupOverviewResourceDtoContent[],
    partialControls: PartialControlForUserListDto
): GroupRow[] {
    return groups.map((groupDto) => {
        const group = groupDto.resource;
        const groupId = group.userGroup.id;

        const calcAssignedAs = (control: PartialControlForUser): AssignedAs => {
            const isAssignee = Object.values(
                control.stepGroup.initialStep
            ).some(
                (assignee) =>
                    (assignee.type === "Group" || assignee.type === "Pool") &&
                    assignee.userGroupId === groupId
            );
            const isReviewer = Object.values(
                control.stepGroup.subsequentSteps["SubsequentStep0"] ?? {}
            ).some(
                (reviewer) =>
                    reviewer.type === "Pool" && reviewer.userGroupId === groupId
            );
            const isSecondReviewer = Object.values(
                control.stepGroup.subsequentSteps["SubsequentStep1"] ?? {}
            ).some(
                (reviewer) =>
                    reviewer.type === "Pool" && reviewer.userGroupId === groupId
            );
            return { isAssignee, isReviewer, isSecondReviewer };
        };

        const controls: ControlView[] = partialControls.content
            .map((dto) => ({ dto, assignedAs: calcAssignedAs(dto.resource) }))
            .filter(
                ({ assignedAs }) =>
                    assignedAs.isAssignee ||
                    assignedAs.isReviewer ||
                    assignedAs.isSecondReviewer
            )
            .map(({ dto, assignedAs }) => {
                const control = dto.resource;
                const programDto =
                    partialControls.linkedResources.controlPrograms.find(
                        (cp) => cp.resource.id === control.controlProgramId
                    )!;
                return {
                    id: control.id,
                    name: control.title,
                    controlUrl: `${WebRoute.CONTROLS}/${control.id}`,
                    canView: canViewControl(dto.actions),
                    programName: programDto.resource.name,
                    programUrl: buildControlProgramViewUrl(
                        programDto.resource.id
                    ),
                    canViewProgram: programDto.actions.view,
                    assignedAs,
                };
            });

        return {
            id: groupId,
            name: group.userGroup.name,
            canEdit: canEditUserGroup(groupDto.actions),
            assignedToControls: controls.length,
            controls,
        };
    });
}

export function useUserGroupsListTable(
    groups: UserGroupOverviewResourceDtoContent[],
    partialControls: PartialControlForUserListDto
) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState<ExpandedState>({});

    const tableData = useMemo(
        () => buildRows(groups, partialControls),
        [groups, partialControls]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");

    const stateAndOnChanges = usePresetTableSearchParams(
        USER_GROUPS_TABLE_STATE_STORAGE_KEY,
        userGroupsListTableDefaultValues,
        userGroupsListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );
    const columns = buildColumns(t);

    const table = useReactTable({
        ...stateAndOnChanges,
        state: {
            ...stateAndOnChanges.state,
            expanded,
        },
        onExpandedChange: setExpanded,
        data: tableData,
        columns,
        getRowId: (row) => row.id.toString(),
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getRowCanExpand: (row) => row.original.controls.length > 0,
        getCoreRowModel: getCoreRowModel<GroupRow>(),
        getSortedRowModel: getSortedRowModel<GroupRow>(),
        getFilteredRowModel: getFilteredRowModel<GroupRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<GroupRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<GroupRow>(),
        getPaginationRowModel: getPaginationRowModel(),
        getExpandedRowModel: getExpandedRowModel<GroupRow>(),
    });

    const filterComponent = (column: Column<GroupRow>) =>
        getFilterComponent(t, column);

    return {
        table,
        getFilterComponent: filterComponent,
        tableId: USER_GROUPS_TABLE_STATE_STORAGE_KEY,
    };
}

const columnHelper = createColumnHelper<GroupRow>();
function buildColumns(t: TFunction) {
    return [
        columnHelper.display({
            id: COLUMN_ID_EXPANDER,
            header: () => null,
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: ({ row }) => (
                <Button
                    size="small"
                    onClick={row.getToggleExpandedHandler()}
                    disabled={!row.getCanExpand()}
                    className={theme.expanderButton}
                >
                    {row.getIsExpanded() ? <MinusOutlined /> : <PlusOutlined />}
                </Button>
            ),
        }),
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: (info) => {
                return (
                    <Checkbox
                        checked={info.table.getIsAllPageRowsSelected()}
                        indeterminate={info.table.getIsSomePageRowsSelected()}
                        onChange={info.table.getToggleAllRowsSelectedHandler()}
                    />
                );
            },
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => {
                const onClick = () => {
                    info.row.toggleSelected();
                };
                return (
                    <Checkbox
                        checked={info.row.getIsSelected()}
                        onClick={onClick}
                    />
                );
            },
        }),
        columnHelper.accessor((row) => row.name, {
            id: USER_GROUPS_LIST_COLUMN_IDS.NAME,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <ProtectedLink
                        href={`${WebRoute.USER_GROUPS}/${row.id}#members`}
                        hasAccess={row.canEdit}
                    >
                        {info.getValue()}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<GroupRow, string[]>((row, value) =>
                tstArrIncludesSomeFilterFn(
                    row,
                    USER_GROUPS_LIST_COLUMN_IDS.NAME,
                    value
                )
            ),
            header: () => t("UserManagement_GroupName"),
            size: TST_COLUMN_SIZE.XXL,
        }),
        columnHelper.accessor((row) => row.assignedToControls, {
            id: USER_GROUPS_LIST_COLUMN_IDS.ASSIGNED_TO_CONTROLS,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            enableGlobalFilter: false,
            sortingFn: Cmp.map(
                (row) => row.original.assignedToControls,
                Cmp.primitive
            ),
            enableColumnFilter: false,
            header: () => t("UserManagement_AssignedToControls"),
            size: TST_COLUMN_SIZE.S,
        }),
    ];
}

type UserGroupsListColumnFilters = ZodCodecMapToType<
    typeof userGroupsListColumnFiltersCodecMap
>;

type UserGroupsFilterableColumnId = keyof UserGroupsListColumnFilters;

function getFilterComponent(t: TFunction, column: Column<GroupRow>) {
    const getFilterHelpers =
        createFilterHelpersGetter<UserGroupsListColumnFilters>(column);
    const columnId = column.id as UserGroupsFilterableColumnId;

    switch (columnId) {
        case USER_GROUPS_LIST_COLUMN_IDS.NAME: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((groupName) => ({
                label: groupName,
                value: groupName,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t("UserManagement_GroupName")}
                    setFilterValue={setFilterValue}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
